import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import {
  Bell, Check, CheckCheck, Trash2, Filter, Loader2,
  AlertCircle, CalendarDays, FileText, Sparkles, Users, Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const CAT_META = {
  shift_change: { label: 'Shift gewijzigd', icon: CalendarDays, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  shift_cancelled: { label: 'Shift geannuleerd', icon: AlertCircle, color: 'text-rose-600 bg-rose-50 border-rose-200' },
  leave_approved: { label: 'Verlof goedgekeurd', icon: Check, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  leave_rejected: { label: 'Verlof afgewezen', icon: AlertCircle, color: 'text-rose-600 bg-rose-50 border-rose-200' },
  document_expiring: { label: 'Document verloopt', icon: FileText, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  roster_published: { label: 'Rooster gepubliceerd', icon: CalendarDays, color: 'text-sky-600 bg-sky-50 border-sky-200' },
  new_application: { label: 'Nieuwe sollicitatie', icon: Users, color: 'text-violet-600 bg-violet-50 border-violet-200' },
  leave_request: { label: 'Verlofaanvraag', icon: CalendarDays, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  document_to_sign: { label: 'Document te tekenen', icon: FileText, color: 'text-sky-600 bg-sky-50 border-sky-200' },
  roster_budget: { label: 'Uren-budget', icon: AlertCircle, color: 'text-rose-600 bg-rose-50 border-rose-200' },
  bug_report: { label: 'Probleemmelding', icon: AlertCircle, color: 'text-orange-600 bg-orange-50 border-orange-200' },
  verzuim_melding: { label: 'Ziekmelding', icon: AlertCircle, color: 'text-rose-600 bg-rose-50 border-rose-200' },
  document_signed: { label: 'Document getekend', icon: FileText, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  roster_guard_weekly: { label: 'Weekcheck rooster', icon: CalendarDays, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  system: { label: 'Systeem', icon: Settings, color: 'text-slate-600 bg-slate-50 border-slate-200' },
  general: { label: 'Algemeen', icon: Bell, color: 'text-brand-navy bg-brand-navy/5 border-brand-navy/20' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'zojuist';
  if (min < 60) return `${min} min geleden`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} uur geleden`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d} dag${d > 1 ? 'en' : ''} geleden`;
  return then.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Meldingen() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread
  const [actionBusy, setActionBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications', {
        params: { only_unread: filter === 'unread' ? true : undefined, limit: 100 },
      });
      setItems(data.items || []);
    } catch (e) {
      toast({ title: 'Meldingen laden mislukt', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter, toast]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    setActionBusy(id);
    try {
      await api.post(`/notifications/${id}/read`);
      await load();
    } catch (e) {
      toast({ title: 'Bijwerken mislukt', variant: 'destructive' });
    } finally {
      setActionBusy(null);
    }
  };

  const markAllRead = async () => {
    setActionBusy('all');
    try {
      const { data } = await api.post('/notifications/read-all');
      toast({ title: `${data.updated} meldingen gemarkeerd als gelezen` });
      await load();
    } catch (e) {
      toast({ title: 'Bijwerken mislukt', variant: 'destructive' });
    } finally {
      setActionBusy(null);
    }
  };

  const remove = async (id) => {
    setActionBusy(id);
    try {
      await api.delete(`/notifications/${id}`);
      setItems((cur) => cur.filter((n) => n.id !== id));
    } catch (e) {
      toast({ title: 'Verwijderen mislukt', variant: 'destructive' });
    } finally {
      setActionBusy(null);
    }
  };

  const unreadCount = items.filter((n) => !n.read_at).length;

  return (
    <div className="space-y-5" data-testid="page-meldingen">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Meldingen</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {unreadCount > 0 ? `${unreadCount} ongelezen` : 'Alles gelezen'}
            {items.length > 0 && ` · ${items.length} totaal`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white text-xs">
            {['all', 'unread'].map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`meldingen-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 border-r last:border-r-0 border-slate-200 transition-colors ${
                  filter === f ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {f === 'all' ? 'Alle' : 'Ongelezen'}
              </button>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={markAllRead}
            disabled={actionBusy === 'all' || unreadCount === 0}
            data-testid="meldingen-mark-all-read"
          >
            {actionBusy === 'all' ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCheck size={14} className="mr-1" />}
            Alles gelezen
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="meldingen-list">
        {loading ? (
          <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Laden…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Bell size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">
              {filter === 'unread' ? 'Geen ongelezen meldingen.' : 'Nog geen meldingen.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((n) => {
              const cat = CAT_META[n.category] || CAT_META.general;
              const Icon = cat.icon;
              const isUnread = !n.read_at;
              return (
                <li
                  key={n.id}
                  data-testid={`melding-${n.id}`}
                  className={`flex items-start gap-3 px-4 py-3 transition-colors ${isUnread ? 'bg-brand-amber/5' : 'hover:bg-slate-50'}`}
                >
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${cat.color}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className={`text-sm ${isUnread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                        {n.title}
                      </h3>
                      <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(n.created_at)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-600 line-clamp-2">{n.body}</p>
                    {n.link && (
                      <a href={n.link} className="mt-1 inline-block text-[11px] text-brand-navy hover:underline">
                        → Bekijk details
                      </a>
                    )}
                    <Badge variant="outline" className="mt-1 text-[10px] border-slate-200 bg-slate-50 text-slate-500">
                      {cat.label}
                    </Badge>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {isUnread && (
                      <button
                        type="button"
                        data-testid={`mark-read-${n.id}`}
                        onClick={() => markRead(n.id)}
                        disabled={actionBusy === n.id}
                        className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50"
                        aria-label="Markeer als gelezen"
                        title="Markeer als gelezen"
                      >
                        {actionBusy === n.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`delete-melding-${n.id}`}
                      onClick={() => remove(n.id)}
                      disabled={actionBusy === n.id}
                      className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      aria-label="Verwijderen"
                      title="Verwijderen"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
